import { useState } from "react";
import { Check, ChevronDown, Plus, RefreshCw, Tv2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useCatalog } from "../contexts/CatalogContext";

interface Props {
  /** Where the menu opens relative to the trigger. Defaults to "up" (desktop sidebar)
   *  but mobile usage should pass "down" so it doesn't fly off the top of the screen. */
  align?: "up" | "down";
}

export function PlaylistSwitcher({ align = "up" }: Props) {
  const { playlists, activePlaylist, setActivePlaylist, refreshCatalog, loading } = useCatalog();
  const [open, setOpen] = useState(false);
  const menuPos =
    align === "down" ? "left-0 right-0 top-full mt-2" : "left-0 right-0 bottom-full mb-2";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface hover:bg-card-hover transition-fast text-left"
      >
        <div className="w-8 h-8 rounded-lg bg-accent-gold/20 text-accent-gold flex items-center justify-center flex-shrink-0">
          <Tv2 size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-ui font-medium text-text-primary truncate">
            {activePlaylist?.name || "No playlist"}
          </p>
          <p className="text-[11px] font-mono text-text-secondary uppercase tracking-[0.04em]">
            {activePlaylist ? activePlaylist.kind : "—"} · {playlists.length} total
          </p>
        </div>
        <ChevronDown
          size={16}
          className={`text-text-secondary transition-fast ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className={`absolute ${menuPos} glass-panel rounded-xl p-2 z-50 max-h-[60vh] overflow-y-auto`}>
          {playlists.length === 0 && (
            <p className="text-[12px] font-ui text-text-secondary p-3">
              No playlists yet. Add one in Settings.
            </p>
          )}
          {playlists.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                setActivePlaylist(p.id);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-card-hover transition-fast text-left"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-ui text-text-primary truncate">{p.name}</p>
                <p className="text-[11px] font-mono text-text-muted uppercase">{p.kind}</p>
              </div>
              {activePlaylist?.id === p.id && (
                <Check size={16} className="text-accent-gold" />
              )}
            </button>
          ))}
          <div className="mt-2 pt-2 border-t border-muted/30 flex flex-col gap-1">
            <Link
              to="/settings"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-card-hover transition-fast text-[13px] font-ui text-text-secondary"
            >
              <Plus size={16} /> Add playlist
            </Link>
            <button
              onClick={() => {
                setOpen(false);
                refreshCatalog(true);
              }}
              disabled={loading || !activePlaylist}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-card-hover transition-fast text-[13px] font-ui text-text-secondary disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh catalog
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
